import { useState, useCallback } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import type { AppLayoutContext } from '~/types';
import CoursesSection from '../SchoolYearView/CoursesSection';

function Courses() {
  const { schoolSlug, yearSlug } = useParams();
  const { school } = useOutletContext<AppLayoutContext>();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);

  const handleCourseClick = useCallback(
    (course: { slug: string }) => {
      navigate(`/app/${schoolSlug}/years/${yearSlug}/courses/${course.slug}`);
    },
    [navigate, schoolSlug, yearSlug]
  );

  if (!school) {
    return <div className="p-6"><p>Loading...</p></div>;
  }

  const schoolYear = school?.schoolYears?.find((y: any) => y.slug === yearSlug);

  if (!schoolYear) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>School Year Not Found</CardTitle>
            <CardDescription>Select a school year to manage courses.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">Courses</h2>
          <p className="text-muted-foreground">{schoolYear.name}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)}>
          <HelpCircle className="h-4 w-4 mr-1" />
          How courses work
        </Button>
      </div>

      <CoursesSection schoolYearId={schoolYear.id} onCourseClick={handleCourseClick} />

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>How Courses Work</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div>
              <h4 className="font-medium text-foreground mb-1">What is a Course?</h4>
              <p>
                A course is a subject offered during this school year — like "Algebra 1" or
                "Theology I". Courses are assigned to one or more terms and can have multiple
                sections (e.g., two sections of Algebra 1 running in Q1).
              </p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Catalog Code</h4>
              <p>
                A short identifier like "ALG-101" that stays consistent across school years.
                When a year's schedule is copied, classes are matched to courses by catalog code
                (falling back to the course name).
              </p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Terms</h4>
              <p>
                Check which terms this course runs in. Most courses run all terms. A semester-only
                elective might only run in Q1 and Q2. Terms must be set up first in School
                Settings.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Sections</h4>
              <p>
                How many parallel classes to create for this course each term. "2 sections" means
                two classes (Section A and Section B) per active term — useful when more students
                need the course than one class can hold.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-1">Grade Levels</h4>
              <p>
                Which grade levels this course is intended for. Used as a filter when
                assigning students to classes — you'll see suggested students based on their
                grade level matching the course.
              </p>
            </div>
            <div className="rounded-lg bg-muted p-3">
              <h4 className="font-medium text-foreground mb-1">What's next?</h4>
              <p>
                After setting up courses, the system will generate <strong>classes</strong> (one per
                section per term). You'll then assign <strong>teachers</strong> and
                <strong> students</strong> to each class, set up <strong>gradebook categories</strong> and
                weights once per Transcript course, and create <strong>assignments</strong>.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Courses;
